import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Loader2, CheckCircle, AlertCircle, Lock, Mail, User, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await axios.get(`${API}/invites/token/${token}`);
        setInvite(response.data);
        if (response.data.name) {
          setFormData(prev => ({ ...prev, name: response.data.name }));
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invite link');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/invites/token/${token}/accept`, {
        name: formData.name,
        password: formData.password
      });
      
      setSuccess(true);
      toast.success(`Welcome to ${response.data.league_name}!`);
      
      // Store the session token in localStorage
      localStorage.setItem('session_token', response.data.session_token);
      
      // Set a flag to indicate fresh login so the app knows to check session
      sessionStorage.setItem('fresh_login', 'true');
      
      // Redirect to root - the app will check session and redirect to appropriate page
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F0E6' }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: '#FF7F50' }} />
          <p style={{ color: '#4682B4' }}>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F0E6' }}>
        <Card className="w-full max-w-md border-2 shadow-xl" style={{ borderColor: '#FF7F50' }}>
          <CardHeader className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#FF7F50' }} />
            <CardTitle style={{ color: '#1E3A4C' }}>Invalid Invite</CardTitle>
            <CardDescription style={{ color: '#4682B4' }}>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => navigate('/login')} 
              className="text-white"
              style={{ backgroundColor: '#FF7F50' }}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F0E6' }}>
        <Card className="w-full max-w-md border-2 shadow-xl" style={{ borderColor: '#FF7F50' }}>
          <CardHeader className="text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#2E7D32' }} />
            <CardTitle style={{ color: '#1E3A4C' }}>Welcome to the League!</CardTitle>
            <CardDescription style={{ color: '#4682B4' }}>
              Redirecting you to your dashboard...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#F5F0E6' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("/sfl-bg.png")`,
        backgroundSize: '400px 400px',
        backgroundRepeat: 'repeat'
      }}></div>
      
      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="/logo-new.png" 
              alt="Survivor Fantasy League" 
              className="w-24 h-24 mx-auto mb-4 drop-shadow-lg"
            />
          </div>

          {/* Invite Card */}
          <Card className="border-2 shadow-xl" style={{ borderColor: '#FF7F50' }}>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(255, 127, 80, 0.15)' }}>
                <Users className="w-8 h-8" style={{ color: '#FF7F50' }} />
              </div>
              <CardTitle className="text-2xl" style={{ color: '#1E3A4C', fontFamily: 'Georgia, serif' }}>
                You're Invited!
              </CardTitle>
              <CardDescription className="mt-2" style={{ color: '#4682B4' }}>
                <span className="font-semibold" style={{ color: '#FF7F50' }}>{invite.inviter_name}</span> has invited you to join
              </CardDescription>
              <p className="text-xl font-bold mt-1" style={{ color: '#1E3A4C', fontFamily: 'Georgia, serif' }}>
                {invite.league_name}
              </p>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" style={{ color: '#1E3A4C' }}>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                    <Input
                      id="email"
                      type="email"
                      value={invite.email}
                      disabled
                      className="pl-10 bg-gray-50"
                      style={{ borderColor: 'rgba(70, 130, 180, 0.3)' }}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="name" style={{ color: '#1E3A4C' }}>Display Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="How should we call you?"
                      required
                      className="pl-10"
                      style={{ borderColor: 'rgba(70, 130, 180, 0.3)' }}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="password" style={{ color: '#1E3A4C' }}>Create Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 8 characters"
                      required
                      minLength={8}
                      className="pl-10 pr-10"
                      style={{ borderColor: 'rgba(70, 130, 180, 0.3)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
                      style={{ color: '#4682B4' }}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword" style={{ color: '#1E3A4C' }}>Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4682B4' }} />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      required
                      className="pl-10"
                      style={{ borderColor: 'rgba(70, 130, 180, 0.3)' }}
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full text-white font-semibold"
                  disabled={submitting}
                  style={{ backgroundColor: '#FF7F50' }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4 mr-2" />
                      Join League
                    </>
                  )}
                </Button>
                
                {/* Google Sign-in option for Gmail users */}
                {invite.email?.toLowerCase().endsWith('@gmail.com') && (
                  <>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t" style={{ borderColor: '#4682B4', opacity: 0.3 }}></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white" style={{ color: '#4682B4' }}>or continue with</span>
                      </div>
                    </div>
                    
                    <a
                      href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(`${window.location.origin}/home#invite_token=${token}`)}`}
                      className="w-full flex items-center justify-center gap-3 bg-white border-2 font-medium px-4 py-3 rounded-lg hover:bg-[#F5F0E6] transition-colors"
                      style={{ borderColor: '#1E3A4C', color: '#1E3A4C' }}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </a>
                  </>
                )}
                
                <p className="text-center text-sm mt-4" style={{ color: '#4682B4' }}>
                  Already have an account?{' '}
                  <a href="/login" className="font-semibold hover:underline" style={{ color: '#FF7F50' }}>
                    Sign in instead
                  </a>
                </p>
              </form>
            </CardContent>
          </Card>
          
          <p className="text-xs text-center mt-4" style={{ color: '#4682B4' }}>
            By joining, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
